import { FC, useContext, useEffect, useRef, useState } from 'react'
import qs from 'qs'
import { connectAutoComplete } from 'react-instantsearch-dom'
import SearchInput from './SearchInput';
import Pill from 'src/common/Pill';
import { Box, Flex, Stack } from '@chakra-ui/react';
import { searchConfigContext } from './SearchableFeedProvider';
import { capitalize } from 'src/util';
import { Caption, P } from 'src/typography/typography';
import Avatar from 'src/common/Avatar';
import { useRouter } from 'next/router';
import { routes } from 'src/navigation/util';
import { useAccent3, useAccent5, useBackground } from 'src/modeAwareColors';

function Hit(props) {

  const {
    onClick,
    renderHit,
    ...rest
  } = props

  const hit = rest

  return (
    <Flex
      mx={1}
      py={2}
      px={3}
      as='li'
      cursor='pointer'
      borderRadius='base'
      onClick={() => onClick(hit)}
      role='group'
      _hover={{
        background: 'success.default',
        color: 'primary.background',
      }}
      _last={{
        // borderBottomLeftRadius: '17px',
        // borderBottomRightRadius: '17px',
        mb: 1,
      }}
      justifyContent='space-between'
      align='center'
    >
      {renderHit(hit)}
    </Flex>
  )
}

const renderMainIndexHit = ({ photoUrl, displayName, type }) => {
  return (
    <>
      <Stack direction='row' spacing={2} align='center'>
        {photoUrl && (
          <Avatar
            src={photoUrl}
            size='2xs'
          />
        )}
        <Box>
          <P>
            {displayName}
          </P>
        </Box>
      </Stack>
      {
        type && (
          <Caption color={useAccent3()} _groupHover={{ color: 'dark.accent8' }}>
            {capitalize(type)}
          </Caption>
        )
      }
    </>
  )
}

const renderMachineCategoriesHit = ({ displayName, parent }) => {
  return (
    <Box>
      <P>
        {displayName}
      </P>
      <Caption _groupHover={{ color: 'dark.accent8' }}>
        {parent}
      </Caption>
    </Box>
  )
}

const FilterableAutocomplete: FC<any> = ({
  hits,
  refine,
  onClickHit,
  topics = [],
  containerProps = {},
  inputGroupProps = {},
  currentRefinement,
  HitComponent = Hit,
  renderHit,
  onChange = (_e) => null,
  ...rest
}) => {

  const {
    createURL,
    indexContextValue,
    hitBoxProps,
    ...props
  } = rest

  const {
    selectedType,
    selectedTopics,
    setSelectedTopics,
  } = useContext(searchConfigContext)

  const router = useRouter()
  const [isFocused, setIsFocused] = useState(true)
  const shouldShowBox = Boolean(isFocused && currentRefinement.length)
  const boxInput = useRef(null)
  const initialInput = useRef(null)
  const firstRender = useRef(true)

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      if (props.autoFocus === false) return // do not focus if autofocus is explictly set to false
    }

    if (shouldShowBox) boxInput.current?.focus()
    if (!shouldShowBox) initialInput.current?.focus()
  }, [shouldShowBox])

  const isSelected = topic => selectedTopics.some(t => t === topic)

  const setTopic = topic => {
    setSelectedTopics(prev => [...prev, topic])
  }

  const unsetTopic = topic => {
    setSelectedTopics(prevState => prevState.filter(t => t !== topic))
  }

  const onSubmitSearch = async () => {
    const queryString = qs.stringify({
      query: currentRefinement,
      topic: selectedTopics[0] || null,
      type: selectedType || null,
    })
    await router.push(`${routes.search}?${queryString}`)
  }

  const onKeyDown = async e => {
    setIsFocused(e.key !== 'Escape')
    if (e.key === 'Enter') {
      await onSubmitSearch()
    }
  }

  const onSelect = topic => isSelected(topic)
    ? unsetTopic(topic)
    : setTopic(topic)

  const _onClickHit = (hit) => {
    onClickHit(hit)
    refine('')
  }

  return (
    <Box position='relative'
      {...containerProps}
    >
      <SearchInput
        refine={refine}
        ref={initialInput}
        borderRadius='md'
        placeholder='What are you looking for?...'
        currentRefinement={currentRefinement}
        onKeyDown={onKeyDown}
        onChange={onChange}
        inputGroupProps={inputGroupProps}
        {...props}
      />
      {shouldShowBox && (
        <>
          <Box
            top={0}
            marginTop='-1px'
            marginLeft='-1px'
            width='100%'
            borderWidth='1px'
            borderRadius='md'
            borderColor={useAccent5()}
            bg={useBackground()}
            boxShadow='searchBox'
            position='absolute'
            zIndex={99}
            {...hitBoxProps}
          >
            <SearchInput
              refine={refine}
              currentRefinement={currentRefinement}
              placeholder='What are you looking for?...'
              borderColor='transparent'
              onKeyDown={onKeyDown}
              ml='1px'
              mb={0}
              ref={boxInput}
              borderWidth={0}
              onChange={onChange}
              containerProps={{
                mt: '-1px',
                ml: '-1px',
              }}
            />
            {hits.length ? hits.map(hit => (
              <HitComponent
                renderHit={renderHit}
                onClick={_onClickHit}
                key={hit.objectID}
                {...hit}
              />
            )) : <Caption textAlign='left' p={4}>No results</Caption>}
          </Box>
        </>
      )}
      {Boolean(topics.length) && (
        <Flex flexWrap='wrap'>
          {topics.map(t => t.name).sort().map(name => (
            <Pill
              isSelected={isSelected(name)}
              onClick={() => onSelect(name)}
              key={name}
              mr={3}
              mb={3}
              _last={{
                mr: 0,
              }}
            >{capitalize(name)}</Pill>
          ))}
        </Flex>
      )}
    </Box>
  )
}

export {
  renderMainIndexHit,
  renderMachineCategoriesHit,
}

export default connectAutoComplete(FilterableAutocomplete)
